import React, { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Clock, AlertTriangle, GraduationCap, ArrowRight } from "lucide-react";

const Kpi = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
        <div className="heading-font text-3xl font-bold mt-1 text-slate-900">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${tone}-50 text-${tone}-600`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

export default function B2COfficerHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/b2c/officer-home").then(r => setData(r.data)); }, []);
  if (!data) return <div>Loading…</div>;
  const c = data.counts;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">My Work</h1>
        <p className="text-sm text-slate-500 mt-1">Hi {user?.name?.split(" ")[0]} — your open items on the B2C pipeline.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Pending Enrollments" value={c.pending_enrollments} icon={GraduationCap} tone="teal" />
        <Kpi label="Overdue Follow-ups" value={c.overdue_followups} icon={AlertTriangle} tone="red" />
        <Kpi label="Approved · Awaiting Enrollment" value={c.awaiting_first_enrollment} icon={Clock} tone="orange" />
      </div>

      <Section title="Approved Filings — Awaiting First Enrollment"
               empty="No approved filings waiting on you." rows={data.awaiting_first_enrollment}>
        {(a) => (
          <button key={a.id} data-testid={`awaiting-${a.application_id}`}
                  onClick={()=>nav(`/b2c/applications/${a.id}`)}
                  className="w-full text-left bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition-shadow flex items-center gap-4">
            <div className="flex-1">
              <div className="mono text-xs text-slate-500">{a.application_id}</div>
              <div className="heading-font font-semibold text-slate-900">{a.student?.full_name}</div>
              <div className="text-xs text-slate-500">{a.college?.name} · {a.program?.name}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400"/>
          </button>
        )}
      </Section>

      <Section title="Pending Enrollment Semesters" empty="No pending enrollments assigned to you." rows={data.pending_enrollments}>
        {(s) => (
          <button key={s.id} data-testid={`pending-sem-${s.id.slice(0,8)}`}
                  onClick={()=>nav(`/b2c/applications/${s.application_id}`)}
                  className="w-full text-left bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow flex items-center gap-4">
            <div className="flex-1">
              <div className="heading-font font-semibold text-slate-900">{s.student?.full_name}</div>
              <div className="text-xs text-slate-500">Semester {s.semester_number} · {s.college?.name} · {s.current_intake?.name}</div>
            </div>
            <span className="stage-badge bg-amber-50 text-amber-900 border-amber-300">PENDING</span>
            <ArrowRight className="w-4 h-4 text-slate-400"/>
          </button>
        )}
      </Section>

      <Section title="Overdue Follow-ups" empty="Nothing overdue." rows={data.overdue_followups}>
        {(a) => (
          <button key={a.id} data-testid={`overdue-${a.application_id}`}
                  onClick={()=>nav(`/b2c/applications/${a.id}`)}
                  className="w-full text-left bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-shadow flex items-center gap-4">
            <div className="flex-1">
              <div className="mono text-xs text-slate-500">{a.application_id}</div>
              <div className="heading-font font-semibold text-slate-900">{a.student?.full_name}</div>
              <div className="text-xs text-slate-500">{a.college?.name} · Due {fmtDate(a.next_followup_date)}</div>
            </div>
            <span className="stage-badge bg-red-50 text-red-900 border-red-300">Overdue</span>
            <ArrowRight className="w-4 h-4 text-slate-400"/>
          </button>
        )}
      </Section>
    </div>
  );
}

const Section = ({ title, empty, rows, children }) => (
  <div className="space-y-2">
    <h2 className="heading-font text-lg font-semibold text-slate-900">{title}</h2>
    <div className="space-y-2">
      {rows.length === 0 ? <div className="bg-white p-6 rounded-lg border border-slate-200 text-sm text-slate-500">{empty}</div> : rows.map(children)}
    </div>
  </div>
);
